// Filter controls for engine-backed reports (reporting foundation R4). The
// server describes each filter (FilterDef) and this renders the matching
// control: date_range → from/to date inputs, select → option picker, run →
// payroll-run picker (non-draft runs, supplied by the parent), text → input.
// Shared by the guided builder and the engine-mode saved viewer.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/features/shared/components/ui/select';
import type { PayrollRun } from '@/features/payroll/types';
import type { ReportFilterDef } from '@/services/reportEngineService';

/** Sentinel for "no selection" — Radix Select items can't have an empty value. */
const ALL = '__all__';

export interface EngineFilterBarProps {
  filters: ReportFilterDef[];
  /** Current values keyed by filter key (date_range uses `from`/`to`). */
  values: Record<string, string>;
  /** Called with '' to clear a key. */
  onChange: (key: string, value: string) => void;
  /** Payroll runs for `run` filters (parent loads them only when needed). */
  runs?: PayrollRun[];
  runsLoading?: boolean;
}

/** Drops empty values — the engine rejects unknown/blank filters. */
export function cleanFilters(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) {
    if (v) out[k] = v;
  }
  return out;
}

export const EngineFilterBar: React.FC<EngineFilterBarProps> = ({
  filters,
  values,
  onChange,
  runs = [],
  runsLoading = false,
}) => {
  const { t } = useTranslation('reports');
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-end gap-4">
      {filters.map((def) => {
        if (def.type === 'date_range') {
          return (
            <React.Fragment key={def.key}>
              <div className="space-y-1.5">
                <Label htmlFor={`engine-filter-from`}>{t('viewer.from', 'From')}</Label>
                <Input
                  id="engine-filter-from"
                  type="date"
                  value={values.from ?? ''}
                  onChange={(e) => onChange('from', e.target.value)}
                  className="w-44"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`engine-filter-to`}>{t('viewer.to', 'To')}</Label>
                <Input
                  id="engine-filter-to"
                  type="date"
                  value={values.to ?? ''}
                  onChange={(e) => onChange('to', e.target.value)}
                  className="w-44"
                />
              </div>
            </React.Fragment>
          );
        }
        if (def.type === 'select') {
          return (
            <div key={def.key} className="space-y-1.5">
              <Label>{def.label}</Label>
              <Select
                value={values[def.key] || ALL}
                onValueChange={(v) => onChange(def.key, v === ALL ? '' : v)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('builder.filterAll', 'All')}</SelectItem>
                  {(def.options ?? []).map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        if (def.type === 'run') {
          return (
            <div key={def.key} className="space-y-1.5">
              <Label>{def.label || t('viewer.payrollRun', 'Payroll run')}</Label>
              <Select
                value={values[def.key] ?? ''}
                onValueChange={(v) => onChange(def.key, v)}
                disabled={runsLoading || runs.length === 0}
              >
                <SelectTrigger className="w-64">
                  <SelectValue
                    placeholder={
                      runsLoading
                        ? t('viewer.loadingRuns', 'Loading runs…')
                        : runs.length === 0
                          ? t('viewer.noRunsShort', 'No locked runs yet')
                          : t('viewer.selectRun', 'Select a run')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {runs.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.periodLabel} — {r.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        // text
        return (
          <div key={def.key} className="space-y-1.5">
            <Label htmlFor={`engine-filter-${def.key}`}>{def.label}</Label>
            <Input
              id={`engine-filter-${def.key}`}
              value={values[def.key] ?? ''}
              onChange={(e) => onChange(def.key, e.target.value)}
              className="w-56"
            />
          </div>
        );
      })}
    </div>
  );
};

export default EngineFilterBar;
